import React, { useEffect, useId, useRef, useState } from "react";
import Icon from "./Icon";
import Popover from "./Popover";
import Modal from "./Modal";
import useBreakpoint from "../../hooks/useBreakpoint";
import "./Menu.scss";

// Fontaine M32 — Menu: action menus and three-dots everywhere (PageHeader actions, table rows, listing
// card, split-button caret). Items with icon, shortcut, danger tone, dividers, submenus. Laptop: popover
// with roving tabindex + type-ahead; phone: bottom-sheet action list. Replaces react-dropdown and ad-hoc menus.

/**
 * @typedef {{ label: string, onSelect?: () => void, icon?: ReactNode, shortcut?: string, danger?: boolean, disabled?: boolean, hint?: string, items?: MenuItem[], divider?: boolean }} MenuItem
 *
 * @param {ReactElement} trigger
 * @param {MenuItem[]} items
 * @param {string} label                       accessible name of the menu
 * @param {"bottom"|"top"|"left"|"right"} [side="bottom"]
 * @param {"start"|"center"|"end"} [align="end"]
 */
export default function Menu({ trigger, items = [], label = "Actions", side = "bottom", align = "end", className = "" }) {
  const { isPhone } = useBreakpoint();
  const [sheetOpen, setSheetOpen] = useState(false);

  if (isPhone) {
    const anchor = React.cloneElement(React.Children.only(trigger), {
      "aria-haspopup": "dialog",
      "aria-expanded": sheetOpen,
      onClick: (e) => { trigger.props.onClick?.(e); setSheetOpen(true); },
    });
    return (
      <>
        {anchor}
        <Modal open={sheetOpen} onClose={() => setSheetOpen(false)} variant="sheet" title={label} className={className}>
          <MenuList items={items} label={label} onDone={() => setSheetOpen(false)} sheet />
        </Modal>
      </>
    );
  }
  return (
    <Popover trigger={trigger} label={label} side={side} align={align} className={`ft-menu__pop ${className}`.trim()}>
      {({ close }) => <MenuList items={items} label={label} onDone={close} />}
    </Popover>
  );
}

function flatten(items) {
  return items.filter((i) => i && !i.divider);
}

export function MenuList({ items, label, onDone, sheet = false }) {
  const id = useId().replace(/:/g, "");
  const listRef = useRef(null);
  const [active, setActive] = useState(0);
  const [openSub, setOpenSub] = useState(null);
  const typed = useRef({ text: "", t: null });
  const enabled = flatten(items).filter((i) => !i.disabled);

  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-index="${active}"]`);
    el?.focus?.();
  }, [active]);

  const run = (item) => {
    if (!item || item.disabled) return;
    if (item.items?.length) { setOpenSub(openSub === item.label ? null : item.label); return; }
    onDone?.();
    item.onSelect?.();
  };

  const onKeyDown = (e) => {
    const n = enabled.length;
    if (!n) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => (a + 1) % n); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => (a - 1 + n) % n); }
    else if (e.key === "Home") { e.preventDefault(); setActive(0); }
    else if (e.key === "End") { e.preventDefault(); setActive(n - 1); }
    else if (e.key === "Enter" || e.key === " ") { e.preventDefault(); run(enabled[active]); }
    else if (e.key === "ArrowRight" && enabled[active]?.items) { e.preventDefault(); setOpenSub(enabled[active].label); }
    else if (e.key === "ArrowLeft" && openSub) { e.preventDefault(); setOpenSub(null); }
    else if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
      // type-ahead: accumulate for 600ms, jump to the first label starting with it
      clearTimeout(typed.current.t);
      typed.current.text += e.key.toLowerCase();
      typed.current.t = setTimeout(() => { typed.current.text = ""; }, 600);
      const idx = enabled.findIndex((i) => i.label.toLowerCase().startsWith(typed.current.text));
      if (idx >= 0) setActive(idx);
    }
  };

  let flat = -1;
  return (
    <ul className={`ft-menu${sheet ? " ft-menu--sheet" : ""}`} role="menu" aria-label={label} ref={listRef} onKeyDown={onKeyDown} id={`ft-menu-${id}`}>
      {items.filter(Boolean).map((item, i) => {
        if (item.divider) return <li key={`d${i}`} role="separator" className="ft-menu__divider" />;
        if (!item.disabled) flat += 1;
        const idx = item.disabled ? -1 : flat;
        const hasSub = !!item.items?.length;
        const subOpen = hasSub && openSub === item.label;
        return (
          <li key={item.label + i} role="none">
            <button
              type="button"
              role={hasSub ? "menuitem" : "menuitem"}
              data-index={idx >= 0 ? idx : undefined}
              tabIndex={idx === active ? 0 : -1}
              aria-disabled={item.disabled || undefined}
              aria-haspopup={hasSub ? "menu" : undefined}
              aria-expanded={hasSub ? subOpen : undefined}
              className={`ft-menu__item${item.danger ? " is-danger" : ""}${item.disabled ? " is-disabled" : ""}${idx === active ? " is-active" : ""}`}
              onMouseEnter={() => { if (idx >= 0) setActive(idx); }}
              onClick={() => run(item)}
            >
              {item.icon ? <span className="ft-menu__icon" aria-hidden="true">{item.icon}</span> : null}
              <span className="ft-menu__label">{item.label}{item.hint ? <span className="ft-menu__hint">{item.hint}</span> : null}</span>
              {item.shortcut ? <kbd className="ft-menu__kbd">{item.shortcut}</kbd> : null}
              {hasSub ? <span className="ft-menu__chev" aria-hidden="true"><Icon name={sheet ? "ChevronDown" : "ChevronRight"} size="xs" /></span> : null}
            </button>
            {subOpen ? (
              <div className="ft-menu__sub">
                <MenuList items={item.items} label={item.label} onDone={onDone} sheet={sheet} />
              </div>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
